import { Injectable } from '@angular/core';
import { RequestService } from './request.service';
import { Observable, Subject, map, of } from 'rxjs';
import { Page } from '../types/page';
import {
  Transaction,
  TransactionType,
  PayinTransactionDto,
  RevertTransactionDto,
  PayinExtraExpenseDto,
  RevertExtraIncomeTransactionDto,
  CessionTransactionDto,
  ExtraIncomeTransactionDto,
  TransactionTypeNames,
  TransactionDirection,
} from '../types/transaction';
import { AccountOwnerType } from '../types/account-owner-type';
import { SearchOptionsDTO } from '../types/search-options';

@Injectable()
export class TransactionService {
  transactionTypes: TransactionType[] = [];
  onTransactionListDataChange: Subject<Transaction[]> = new Subject<
    Transaction[]
  >();
  onSubmitPayment = new Subject<number>();
  dateChanged: Subject<Date> = new Subject<Date>();
  reloadSummary: Subject<Date> = new Subject<Date>();
  updateCreditTransactionList = new Subject<void>();

  constructor(private request: RequestService) { }

  getTransactionList$(
    options?: SearchOptionsDTO
  ): Observable<Page<Transaction>> {
    return this.request.get(['transactions'], { params: options })
    .pipe(
      map((transactionPage: Page<Transaction>) => {
        transactionPage.content.map(this._mapTransactionDirection.bind(this));
        return transactionPage;
      }),
    );
  }

  getTransactionTypes$(): Observable<TransactionType[]> {
    return this.request.get(['transactions', 'types']);
  }

  getTransactionById$(id: number | string): Observable<Transaction> {
    return this.request.get(['transactions', id]);
  }

  saveTransaction$(transaction: Transaction): Observable<any> {
    return this.request.post(['transactions'], { body: transaction });
  }

  editPayInTransactionAccount$(transactionId: number, accountId: number): Observable<Transaction> {
    return this.request.post(['transactions', transactionId, 'account'], {
      body: accountId,
    })
    .pipe(
      map(this._mapTransactionDirection.bind(this))
    );
  }

  revertPayinTransaction$(dto: RevertTransactionDto): Observable<Transaction> {
    return this.request.post(['transactions', 'pay-ins', dto.transactionId, 'revert'], {
      body: dto,
    });
  }

  revertPayoutTransaction$(transactionId: number) {
    return this.request.post(['transactions', 'pay-outs', transactionId, 'reverted'])
  }

  revertExtraIncomeTransaction$(
    dto: RevertExtraIncomeTransactionDto
  ): Observable<Transaction> {
    return this.request.post(['transactions', 'revert', 'extra-income'], {
      body: dto,
    });
  }

  payoutTransaction$(transaction: Transaction): Observable<any> {
    return this.request.post(['transactions', 'pay-outs'], {
      body: transaction,
    });
  }

  editPayoutTransactionAccount$(transactionId: number, sourceAccountId: number) {
    return this.request.post(['transactions', 'pay-outs', transactionId, 'account'], {
      body: {
        accountId: sourceAccountId
      },
    });
  }

  payinTtransaction$(transaction: PayinTransactionDto): Observable<any> {
    return this.request.post(['transactions', 'pay-ins'], {
      body: transaction,
    });
  }

  payinExtraIncome$(dto: ExtraIncomeTransactionDto) {
    return this.request.post(['transactions', 'pay-ins', 'extra-income'], {
      body: dto,
    });
  }

  payinExtraExpense$(dto: PayinExtraExpenseDto): Observable<any> {
    return this.request.post(['transactions', 'pay-ins', 'extra-expense'], {
      body: dto,
    });
  }

  payinCession$(dto: CessionTransactionDto): Observable<any> {
    return this.request.post(['transactions', 'pay-ins', 'cession'], {
      body: dto,
    });
  }

  confirmPayoutTransaction$(transaction: Transaction): Observable<Transaction> {
    return this.request.post([
      'transactions',
      'pay-outs',
      transaction.id,
      'confirm',
    ]);
  }

  confirmPayinTransaction$(transaction: Transaction): Observable<Transaction> {
    return this.request.post(
      ['transactions', 'pay-ins', transaction.id, 'confirm'],
    );
  }

  cancelPayoutTransaction$(id: number | string): Observable<Transaction> {
    return this.request.post(['transactions', 'pay-outs', id, 'cancel']);
  }

  getDisallowedAccountTypes(accountType: string) {
    switch (accountType) {
      case 'COMPANY':
        return [];

      case 'CLIENT':
        return [TransactionTypeNames.CLIENT_IN, TransactionTypeNames.CLIENT_OUT, 'EXTERNAL'];

      case 'CLIENT_VIRTUAL':
        return ['CLIENT_VIRTUAL'];

      case 'EXTERNAL':
        return ['EXTERNAL', TransactionTypeNames.CLIENT_IN, TransactionTypeNames.CLIENT_OUT];

      default:
        return [];
    }
  }

  payoutRefinanceTransaction$(transaction: Transaction): Observable<any> {
    return this.request.post(['transactions', 'refinances'], {
      body: transaction,
    });
  }

  _mapTransactionDirection(transaction: Transaction) {
    if (transaction.type.name === TransactionTypeNames.EXTRA_IN && transaction.amount < 0) {
      transaction.direction = TransactionDirection.outgoing;
    } else if (transaction.type.name.endsWith('_IN') || transaction.type.name === TransactionTypeNames.DEBT_CANCELLATION) {
      transaction.direction = TransactionDirection.incoming;
    } else if (transaction.type.name.endsWith('_OUT')) {
      transaction.direction = TransactionDirection.outgoing;
    } else {
      transaction.direction = null;
    }
    return transaction;
  }

  // Not used..
  getAllowedTransactionTypes(
    accountSenderType: AccountOwnerType,
    accountRecieverType: AccountOwnerType
  ): TransactionType[] {
    switch (accountSenderType.name) {
      case 'CLIENT':
        switch (accountRecieverType.name) {
          case 'CLIENT_VIRTUAL':
            return [
              this.transactionTypes.find(
                transactionType => transactionType.name === TransactionTypeNames.CLIENT_IN || TransactionTypeNames.CLIENT_OUT
              ),
            ];
          case 'COMPANY':
            return [
              this.transactionTypes.find(
                transactionType => transactionType.name === TransactionTypeNames.EXTRA_IN || TransactionTypeNames.DEBT_CANCELLATION
              ),
              this.transactionTypes.find(
                transactionType => transactionType.name === 'MISC' // MISC Doesnt exist
              ),
            ];

          default:
            return [];
        }

      case 'CLIENT_VIRTUAL':
        switch (accountRecieverType.name) {
          case 'CLIENT':
            return [
              this.transactionTypes.find(
                transactionType => transactionType.name === TransactionTypeNames.CLIENT_IN || TransactionTypeNames.CLIENT_OUT
              ),
            ];

          case 'COMPANY':
            return [
              this.transactionTypes.find(
                transactionType => transactionType.name === TransactionTypeNames.CREDIT_IN || TransactionTypeNames.CREDIT_OUT
              ),
              this.transactionTypes.find(
                transactionType => transactionType.name === TransactionTypeNames.REFINANCE_IN || TransactionTypeNames.REFINANCE_OUT
              ),
              this.transactionTypes.find(
                transactionType => transactionType.name === TransactionTypeNames.EXTRA_IN || TransactionTypeNames.DEBT_CANCELLATION
              ),
            ];

          case 'EXTERNAL':
            return [
              this.transactionTypes.find(
                transactionType => transactionType.name === TransactionTypeNames.CLIENT_IN || TransactionTypeNames.CLIENT_OUT
              ),
            ];

          default:
            return [];
        }

      case 'COMPANY':
        switch (accountRecieverType.name) {
          case 'CLIENT':
            return [
              this.transactionTypes.find(
                transactionType => transactionType.name === TransactionTypeNames.EXTRA_IN || TransactionTypeNames.DEBT_CANCELLATION
              ),
              this.transactionTypes.find(
                transactionType => transactionType.name === 'MISC' // MISC Doesnt exist
              ),
              this.transactionTypes.find(
                transactionType => transactionType.name === TransactionTypeNames.CREDIT_IN || TransactionTypeNames.CREDIT_OUT
              ),
            ];
          case 'CLIENT_VIRTUAL':
            return [
              this.transactionTypes.find(
                transactionType => transactionType.name === TransactionTypeNames.REFINANCE_IN || TransactionTypeNames.REFINANCE_OUT
              ),
              this.transactionTypes.find(
                transactionType => transactionType.name === TransactionTypeNames.EXTRA_IN || TransactionTypeNames.DEBT_CANCELLATION
              ),
              this.transactionTypes.find(
                transactionType => transactionType.name === TransactionTypeNames.CREDIT_IN || TransactionTypeNames.CREDIT_OUT
              ),
            ];
          case 'COMPANY':
            return [
              this.transactionTypes.find(
                transactionType => transactionType.name === TransactionTypeNames.INTERNAL_TRANSFER_IN || TransactionTypeNames.INTERNAL_TRANSFER_OUT
              ),
            ];

          case 'EXTERNAL':
            return [
              this.transactionTypes.find(
                transactionType => transactionType.name === TransactionTypeNames.EXTRA_IN || TransactionTypeNames.DEBT_CANCELLATION
              ),
              this.transactionTypes.find(
                transactionType => transactionType.name === 'MISC' // MISC Doesnt exist
              ),
              this.transactionTypes.find(
                transactionType => transactionType.name === TransactionTypeNames.CREDIT_IN || TransactionTypeNames.CREDIT_OUT
              ),
            ];

          default:
            return [];
        }

      case 'EXTERNAL':
        switch (accountRecieverType.name) {
          case 'CLIENT_VIRTUAL':
            return [
              this.transactionTypes.find(
                transactionType => transactionType.name === TransactionTypeNames.CLIENT_IN || TransactionTypeNames.CLIENT_OUT
              ),
            ];
          case 'COMPANY':
            return [
              this.transactionTypes.find(
                transactionType => transactionType.name === TransactionTypeNames.EXTRA_IN || TransactionTypeNames.DEBT_CANCELLATION
              ),
              this.transactionTypes.find(
                transactionType => transactionType.name === 'MISC' // MISC Doesnt exist
              ),
            ];

          default:
            return [];
        }

      default:
        return [];
    }
  }
}
